import CheckIcon from '@mui/icons-material/Check';
import { Grid, Typography } from '@mui/material';
import Badge from '@mui/material/Badge';
import { Theme } from '@mui/material/styles';
import * as React from 'react';
import { shallowEqual } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { GenericReducer, genericReducer } from '../../../sic-lib/reducers/genericReducer';
import { FileItemInfo } from '../../../sic-lib/types/FileItemInfo';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
    addSelectedMedia,
    removeSelectedMedia,
    setMaxAttachmentReached,
} from '../../../store/media';

interface Props {
    data: FileItemInfo;
}

export default React.memo(function SicMediaTabItem({ data }: Props) {
    const { classes } = useStyles();
    const dispatch = useAppDispatch();

    const store = useAppSelector(state => {
        return {
            selected: state.media?.selectedMedia,
        };
    }, shallowEqual);

    const [compState, compDispatch] = React.useReducer<GenericReducer<ComponentState>>(
        genericReducer,
        {
            file: data,
            isSelected: store.selected?.some(file => file.fileName === data.fileName) || false,
        },
    );

    const getFileExtension = () => {
        return (
            compState.file.fileName.substring(
                compState.file.fileName.lastIndexOf('.'),
                compState.file.fileName.length,
            ) || compState.file.fileName
        );
    };

    const handleSelect = () => {
        if (store.selected.length === 10 && !compState.isSelected) {
            dispatch(setMaxAttachmentReached({ maxAttachmentReached: true }));
            setTimeout(() => {
                dispatch(setMaxAttachmentReached({ maxAttachmentReached: false }));
            }, 10000);
        } else if (compState.isSelected) {
            compDispatch({ isSelected: false });
            dispatch(removeSelectedMedia({ fileName: compState.file.fileName }));
        } else {
            compDispatch({ isSelected: true });
            dispatch(addSelectedMedia({ selectedMedia: [compState.file] }));
        }
    };

    return (
        <Grid
            container
            flexDirection="column"
            alignItems="center"
            justifyContent="flex-end"
            flexWrap="nowrap"
            onClick={handleSelect}
            className={classes.container}
        >
            <Grid item height={200} display="flex" alignItems="center" justifyContent="center">
                <Badge
                    badgeContent={<CheckIcon sx={{ fontSize: 15 }} />}
                    color="primary"
                    invisible={!compState.isSelected}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    {(compState.file.mimeType === 'image/jpeg' ||
                        compState.file.mimeType === 'image/webp' ||
                        compState.file.mimeType === 'image/png' ||
                        compState.file.mimeType === 'image/gif') && (
                        <img src={compState.file.url} alt="SMS file" className={classes.media} />
                    )}
                    {compState.file.mimeType === 'video/mp4' && (
                        <video
                            src={compState.file.url}
                            preload="metadata"
                            className={classes.media}
                        />
                    )}
                    {(compState.file.mimeType === 'audio/mpeg' ||
                        compState.file.mimeType === 'audio/mp4' ||
                        compState.file.mimeType === 'application/octet-stream') && (
                        <audio src={compState.file.url} controls className={classes.media} />
                    )}
                    {compState.file.mimeType !== 'image/jpeg' &&
                        compState.file.mimeType !== 'image/webp' &&
                        compState.file.mimeType !== 'image/png' &&
                        compState.file.mimeType !== 'image/gif' &&
                        compState.file.mimeType !== 'video/mp4' &&
                        compState.file.mimeType !== 'audio/mpeg' &&
                        compState.file.mimeType !== 'application/octet-stream' &&
                        compState.file.mimeType !== 'audio/mp4' && (
                            <Typography className={classes.txtExtension}>
                                {getFileExtension()}
                            </Typography>
                        )}
                </Badge>
            </Grid>
            <Typography variant="caption" display="block" align="center" width={200} noWrap>
                {compState.file.fileName}
            </Typography>
        </Grid>
    );
});

interface ComponentState {
    file: FileItemInfo;
    isSelected: boolean;
}

const useStyles = makeStyles()((theme: Theme) => ({
    container: {
        borderRadius: 4,
        height: 230,
        width: 230,
        cursor: 'pointer',
    },
    media: {
        maxWidth: '200px',
        maxHeight: '200px',
    },
    button: {
        color: '#fff',
    },
    txtExtension: {
        backgroundColor: theme.palette.background.default,
        padding: theme.spacing(0.5),
        borderRadius: 4,
    },
}));
